/**
 * BranchSwitcher — the header control that changes the session's active branch.
 *
 * Switching is a SERVER operation (`POST /api/v1/auth/switch-branch`), not a
 * client filter, because the effective permission set and the data scope are
 * derived per branch: a shift leader may hold `order.cancel` in one branch and
 * not in another. After the switch the whole `/auth/me` payload is re-fetched
 * and every cached query is invalidated — showing branch A's orders under
 * branch B's header would be worse than a moment of loading.
 *
 * A user with a tenant-wide scope sees every active branch; a branch-scoped
 * user sees only their assigned ones. That list comes from the API, not from
 * a client-side filter.
 */
import { useQueryClient } from '@tanstack/react-query';
import { Select, useToast } from '@sonauto/ui';
import { errorMessage } from '../lib/api.js';
import { useAuth } from '../auth/AuthProvider.js';

export function BranchSwitcher(): React.JSX.Element | null {
  const { branches, activeBranchId, switchBranch } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();

  if (branches.length === 0) return null;

  // A single branch is not a choice; render it as a label instead of a control.
  if (branches.length === 1) {
    const only = branches[0];
    return (
      <span className="inline-flex items-center gap-2 rounded-md bg-surface-alt px-3 py-1.5 text-sm font-medium text-fg">
        <span aria-hidden="true">🏬</span>
        {only?.name ?? '—'}
      </span>
    );
  }

  return (
    <label className="flex items-center gap-2">
      <span className="sr-only">Chi nhánh đang xem</span>
      <span aria-hidden="true">🏬</span>
      <Select
        className="min-w-[12rem]"
        value={activeBranchId ?? ''}
        placeholder={activeBranchId === null ? 'Chọn chi nhánh' : undefined}
        options={branches.map((b) => ({ value: b.id, label: `${b.name} (${b.code})` }))}
        onChange={(event) => {
          const next = event.target.value;
          if (next === '' || next === activeBranchId) return;
          void switchBranch(next)
            .then(async () => {
              // Everything cached was scoped to the previous branch.
              await queryClient.invalidateQueries();
              toast.success('Đã chuyển chi nhánh');
            })
            .catch((error: unknown) => toast.error('Không chuyển được chi nhánh', errorMessage(error)));
        }}
      />
    </label>
  );
}
